import { Button } from "@/components/ui-kit/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

type RentPaymentSettingsRowProps = {
  isRecurrent: boolean
  onRecurrentChange: (value: boolean) => void
  promoDraft: string
  onPromoDraftChange: (value: string) => void
  onApplyPromo: () => void
  appliedPromo?: string
  promoError?: string | null
  disabled?: boolean
  isSubmitting?: boolean
  isApplying?: boolean
}

function getRecurrentSegmentClassName(active: boolean) {
  return cn(
    "flex w-full items-center justify-center rounded px-4 py-2 text-base transition-shadow lg:text-[18px] lg:leading-[24px] text-[16px] leading-[22px]",
    active
      ? "bg-white text-brand shadow-[0_2px_5px_rgba(0,0,0,0.2)]"
      : "text-light-muted",
  )
}

export function RentPaymentSettingsRow({
  isRecurrent,
  onRecurrentChange,
  promoDraft,
  onPromoDraftChange,
  onApplyPromo,
  appliedPromo = "",
  promoError = null,
  disabled = false,
  isSubmitting = false,
  isApplying = false,
}: RentPaymentSettingsRowProps) {
  const isToggleDisabled = disabled || isSubmitting
  const normalizedDraft = promoDraft.trim().toUpperCase()
  const canApply =
    !disabled &&
    !isSubmitting &&
    !isApplying &&
    normalizedDraft.length > 0 &&
    normalizedDraft !== appliedPromo

  return (
    <section
      className={cn(
        "flex min-w-0 flex-col gap-6 transition-opacity lg:flex-row lg:items-start justify-between lg:justify-normal",
        disabled && "pointer-events-none opacity-50",
      )}
      aria-disabled={disabled}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h4 className="lg:text-lg text-[18px] leading-[22px] text-foreground">Автоплатёж</h4>
        </div>

        <div
          className={cn(
            "grid w-full lg:max-w-[280px] max-w-full grid-cols-2 gap-1 self-start rounded-lg bg-muted p-1 h-[60px]",
            isToggleDisabled && "pointer-events-none opacity-60",
          )}
          role="group"
          aria-label="Автоплатёж"
        >
          <button
            type="button"
            className={getRecurrentSegmentClassName(isRecurrent)}
            aria-pressed={isRecurrent}
            disabled={isToggleDisabled}
            onClick={() => onRecurrentChange(true)}
          >
            Вкл.
          </button>
          <button
            type="button"
            className={getRecurrentSegmentClassName(!isRecurrent)}
            aria-pressed={!isRecurrent}
            disabled={isToggleDisabled}
            onClick={() => onRecurrentChange(false)}
          >
            Выкл.
          </button>
        </div>

        <p className="text-[12px] leading-[16px] text-light-muted">
          Подключите автоплатеж – <br />
          экономьте время на оплате боксов
        </p>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-3 lg:max-w-[420px]">
        <p className="lg:text-[16px] text-[18px] leading-[20px] font-medium text-foreground">
          Есть промокод?
        </p>
        <div className="flex min-w-0 gap-2 flex-col lg:flex-row">
          <Input
            id="rent-promo"
            value={promoDraft}
            onChange={(event) => onPromoDraftChange(event.target.value.toUpperCase())}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault()
                if (canApply) {
                  onApplyPromo()
                }
              }
            }}
            placeholder="Промокод"
            autoComplete="off"
            disabled={disabled || isSubmitting}
            className="!h-[60px] min-w-0 lg:flex-1 rounded-lg border-0 bg-input px-4 text-[18px] leading-6 shadow-none focus-visible:ring-2 focus-visible:ring-ring/50 lg:max-w-[280px]"
          />
          <Button
            type="button"
            variant="primary"
            className="h-[60px] shrink-0 px-4 text-[18px] leading-6 lg:px-6 lg:w-auto"
            disabled={!canApply}
            showArrow={false}
            onClick={onApplyPromo}
          >
            {isApplying ? "..." : "Применить"}
          </Button>
        </div>
        {promoError ? <p className="text-sm text-destructive">{promoError}</p> : null}
        {!promoError && appliedPromo ? (
          <p className="text-sm text-light-muted">Применён промокод: {appliedPromo}</p>
        ) : null}
      </div>
    </section>
  )
}
